import { SelectedPackages } from 'context/SingleServiceManagerContext';
import { useState, useRef, useEffect, RefObject } from 'react';
import { HelpCircleOutline } from 'react-ionicons';
import { usePopper } from 'react-popper';

const useTooltip = () => {
  const triggerEltRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  return {
    triggerEltRef,
    isVisible,
    handleEnter: () => setIsVisible(true),
    handleLeave: () => setIsVisible(false),
  };
};

type TooltipProps = {
  triggerEltRef: RefObject<HTMLDivElement>;
  isVisible: boolean;
  title: string;
  body: React.ReactNode;
};

const Tooltip = ({ triggerEltRef, isVisible, title, body }: TooltipProps) => {
  const popperEltRef = useRef<HTMLDivElement>(null);
  const arrowEltRef = useRef<HTMLDivElement>(null);

  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (popperEltRef.current) setPopperElement(popperEltRef.current);
    if (arrowEltRef.current) setArrowElement(arrowEltRef.current);
  }, [isVisible]);

  const { styles, attributes } = usePopper(
    triggerEltRef.current,
    popperElement,
    {
      placement: 'bottom-start',
      modifiers: [
        { name: 'offset', options: { offset: [0, 10] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
        { name: 'preventOverflow', options: { boundary: 'clippingParents' } },
        { name: 'arrow', options: { element: arrowElement } },
      ],
    },
  );

  if (!isVisible) return null;

  return (
    <div ref={popperEltRef} style={styles.popper} {...attributes.popper}>
      <div className="z-50 max-w-[328px] rounded-lg bg-background-base-surface-0 p-5 text-content-base-subdued shadow-elevation3">
        <p className="text-base font-bold text-content-base-default">{title}</p>
        <p className="mt-1 whitespace-normal text-sm font-medium">{body}</p>
      </div>
    </div>
  );
};

interface NetworkMonitoringLabelProps {
  mns: SelectedPackages;
}

export const NetworkMonitoringLabel = ({
  mns,
}: NetworkMonitoringLabelProps) => {
  const { triggerEltRef, isVisible, handleEnter, handleLeave } = useTooltip();

  return (
    <>
      <div
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className="inline-flex items-center gap-1 whitespace-nowrap"
      >
        <span>Network&nbsp;monitoring</span>
        <span ref={triggerEltRef}>
          <HelpCircleOutline color="currentColor" width="24px" height="24px" />
        </span>
      </div>

      <Tooltip
        triggerEltRef={triggerEltRef}
        isVisible={isVisible}
        title="Network monitoring"
        body={
          mns.essential ? (
            <>
              Granite’s <b>MNS Essentials</b> bundle provides end-to-end network
              management. Includes ping monitoring for basic network performance
              insights and the edgeboot service bundle.
            </>
          ) : (
            <>
              Granite’s <b>MNS Enhanced</b> bundle provides end-to-end network
              management. Includes advanced network telemetry for detailed
              performance insights, API access, and the edgeboot service bundle.
            </>
          )
        }
      />
    </>
  );
};
